/**
 * Reconnaître un titre qui ne dit rien.
 *
 * Certains sites répondent à un lecteur de métadonnées par leur propre nom et rien d'autre :
 * une story Facebook partagée donne « Facebook », sans description ni image, parce que la page
 * est derrière un mur de connexion. La fiche s'appelait donc « Facebook », et plus rien ne
 * permettait de savoir quelle recette c'était.
 *
 * La règle est volontairement unique plutôt qu'une liste de sites à tenir à jour : <b>un titre
 * qui répète le nom du site n'apprend rien</b>. Elle vaut pour Facebook comme pour le prochain
 * site qui fera pareil.
 */

/** Minuscules, sans accents ni ponctuation : « Facebook » et « facebook.com » se comparent. */
function normaliser(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

/**
 * Le nom propre d'un domaine : « facebook » pour « www.facebook.com ».
 *
 * Les préfixes de service et l'extension sont retirés ; ce qui reste est ce que le site
 * s'appelle.
 */
function nomDuSite(domaine: string): string {
  const morceaux = domaine
    .toLowerCase()
    .replace(/^(www|m|mobile|fr|web)\./, '')
    .split('.')

  return normaliser(morceaux[0] ?? '')
}

/**
 * Vrai quand ce titre n'apprend rien sur le contenu de la page.
 *
 * @param titre le titre lu sur la page, ou celui déjà posé sur une fiche.
 * @param domaine le domaine d'où il vient.
 */
export function titreGenerique(titre: string | null | undefined, domaine: string | null | undefined): boolean {
  const propre = (titre ?? '').trim()

  // Un titre absent ou d'un ou deux caractères ne distingue aucune fiche d'une autre.
  if (normaliser(propre).length <= 2) {
    return true
  }

  if (!domaine) {
    return false
  }

  const site = nomDuSite(domaine)

  if (site.length === 0) {
    return false
  }

  const compare = normaliser(propre)

  // « Facebook », « facebook.com », « Facebook – Connexion » : le titre ne fait que nommer le
  // site. Au-delà, il dit quelque chose — « Cooki Kitchen » n'est pas « facebook ».
  return compare === site || compare === normaliser(domaine) || compare === `${site}connexion`
}
